const HTML_ESCAPES = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
};
/**
 * Escape HTML special characters to prevent XSS when interpolating untrusted
 * values into HTML output (email bodies, IdP-assertion echoes, rendered rich
 * text, etc.). Escapes `& < > " '`; the apostrophe uses the `&#39;` numeric
 * entity (valid in both HTML attributes and text). Single-pass over the input.
 */
export function escapeHtml(str) {
    return str.replace(/[&<>"']/g, (c) => HTML_ESCAPES[c] ?? c);
}
/** Escape a string for use as a literal inside a `RegExp` pattern. */
export function escapeRegex(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
/**
 * Truncate `s` to at most `max` characters, appending an ellipsis ('…') when
 * it was cut. The ellipsis counts toward the budget (result length ≤ `max`),
 * and any whitespace left dangling at the cut is trimmed before the ellipsis.
 * Strings already within `max` are returned unchanged.
 */
export function truncate(s, max) {
    if (s.length <= max)
        return s;
    return s.slice(0, max - 1).trimEnd() + '…';
}
/**
 * Converts a string to title case (e.g., "united states" -> "United States")
 * Handles common edge cases like all-caps input and mixed case
 */
export function toTitleCase(str) {
    if (!str)
        return '';
    // List of words that should remain lowercase (unless first word)
    const lowercaseWords = new Set([
        'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'nor',
        'of', 'on', 'or', 'so', 'the', 'to', 'up', 'yet'
    ]);
    return str
        .toLowerCase()
        .split(' ')
        .map((word, index) => {
        if (!word)
            return word;
        // Always capitalize first word, or words not in the lowercase list
        if (index === 0 || !lowercaseWords.has(word)) {
            return word.charAt(0).toUpperCase() + word.slice(1);
        }
        return word;
    })
        .join(' ');
}
/**
 * First name from a person's display name, for a reply sign-off ("Best,\nSarah").
 * Returns null when there is nothing usable to sign with — the caller then falls
 * back to a neutral close rather than signing with something wrong.
 *
 * Scope: OUR OWN users (a logged-in teammate), whose display name is built by the
 * auth layer as `first_name + ' ' + last_name`, falling back to the email address
 * when neither is set. So the first whitespace token is the given name, and the
 * "Phillips, John" ordering that email `From` headers produce cannot occur here.
 * Customer names are the other case entirely and have their own parser — see
 * `deriveRequesterFirstName` in the helpdesk backend, which additionally skips
 * role mailboxes and can derive a name from an email local part. Don't cross the
 * two: that parser's email fallback would sign a reply with OUR support address.
 *
 * An email address is treated as no-name (the auth fallback shape): a reply must
 * never sign off "Best,\nsupport@acme.com".
 */
export function getFirstName(displayName) {
    const name = displayName?.trim();
    if (!name)
        return null;
    if (name.includes('@'))
        return null; // auth's email fallback — not a name
    const first = name.split(/\s+/)[0].replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
    return first || null;
}
/** Closes a model may produce, with nothing following them. */
const BARE_CLOSE = /^(best|best regards|kind regards|regards|warm regards|thanks|thank you|many thanks|sincerely|cheers)\s*[,.]?\s*$/i;
/**
 * Put a sender's name on the end of an AI-drafted reply.
 *
 * AI drafts are written before the sender is known — reply-gen runs in the inbound
 * pipeline before anyone picks the ticket up, and the Conductor drafts before its
 * proposal is approved (or auto-sent). Both close with a bare "Best," and leave the
 * name to whoever turns out to be sending (INV-AI-6/7 in
 * `apps/helpdesk/docs/features/brand-and-channels/per-brand-ai-agent.md`).
 *
 * `name` is written VERBATIM — the caller decides what it is: a teammate's first
 * name (via `getFirstName`) when a person sends, the brand's AI persona when the AI
 * sends unattended. A null/blank name leaves the draft alone, so an unresolvable
 * sender yields the bare close rather than a guessed signature (INV-AI-8).
 *
 * Only a close with nothing after it gets a name. A draft that already ends with one
 * — the model ignored its instruction, a human typed it, or this ran once already —
 * is returned untouched, so a reply can never go out signed twice. That also makes
 * the function idempotent, which is what lets the composer sign a draft for preview
 * and the send path sign it again without producing two names.
 */
export function signReplyWithName(draft, name) {
    const signature = name?.trim();
    if (!draft || !signature)
        return draft;
    const last = findBareCloseLine(draft);
    if (last === null)
        return draft;
    const lines = draft.split('\n');
    lines.splice(last + 1, 0, signature);
    return lines.join('\n');
}
/**
 * Turn a nameless close into a neutral one — "Best," becomes "Best regards,".
 *
 * The send path's partner to `signReplyWithName`, for when no name resolved: a reply
 * must not go out with a sign-off line and nothing under it, which reads as a
 * truncated email. Neutral, never a guessed or borrowed name (INV-AI-8).
 *
 * Deliberately NOT applied when a draft opens in an editor: there the operator is
 * looking at it and may still type a name, so the bare close is left as the model
 * wrote it. Only the layer that actually sends commits to the fallback.
 */
export function closeReplyNeutrally(draft) {
    const last = findBareCloseLine(draft);
    if (last === null)
        return draft;
    const lines = draft.split('\n');
    if (/^best regards\s*[,.]?\s*$/i.test(lines[last].trim()))
        return draft;
    lines[last] = 'Best regards,';
    return lines.join('\n');
}
/** Index of the draft's trailing bare close, or null when it doesn't end with one. */
function findBareCloseLine(draft) {
    if (!draft)
        return null;
    const lines = draft.split('\n');
    let last = lines.length - 1;
    while (last >= 0 && lines[last].trim() === '')
        last--;
    if (last < 0)
        return null;
    return BARE_CLOSE.test(lines[last].trim()) ? last : null;
}
//# sourceMappingURL=string.js.map